// src/services/annonceService.js
// Couche service — opérations Firebase pour les annonces communautaires

import { db, storage, functions } from '../firebase';
import {
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  collection,
  query,
  where,
  orderBy,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import {
  ANNONCE_CATEGORIES,
  ANNONCE_MAX_ACTIVE,
  ANNONCE_MAX_PHOTOS,
  ANNONCE_MAX_DESCRIPTION,
  ANNONCE_DURATION_DAYS,
} from '../constants';
import { calculateAge } from '../utils';

const VALID_CATEGORIES = ANNONCE_CATEGORIES.map((c) => c.id);

const annonceService = {
  // ── Créer une annonce ─────────────────────────────────
  async createAnnonce(userId, data) {
    try {
      // Vérifier la majorité pour créer une annonce
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (userDoc.exists()) {
        const age = calculateAge(userDoc.data().dateOfBirth);
        if (age !== null && age < 18) {
          return { success: false, error: 'Vous devez avoir 18 ans pour publier une annonce.' };
        }
      }

      if (!data.title || !data.title.trim()) {
        return { success: false, error: 'title_required' };
      }
      if (!data.description || !data.description.trim()) {
        return { success: false, error: 'description_required' };
      }
      if (data.description.length > ANNONCE_MAX_DESCRIPTION) {
        return { success: false, error: 'description_too_long' };
      }
      if (!data.category || !VALID_CATEGORIES.includes(data.category)) {
        return { success: false, error: 'invalid_category' };
      }
      if (!data.city || !data.city.trim()) {
        return { success: false, error: 'city_required' };
      }
      if (data.photos && data.photos.length > ANNONCE_MAX_PHOTOS) {
        return { success: false, error: 'too_many_photos' };
      }

      // Vérifier max 5 annonces actives
      const activeQuery = query(
        collection(db, 'annonces'),
        where('authorId', '==', userId),
        where('status', '==', 'active')
      );
      const activeSnap = await getDocs(activeQuery);
      if (activeSnap.size >= ANNONCE_MAX_ACTIVE) {
        return { success: false, error: 'max_active_reached' };
      }

      const now = Timestamp.now();
      const expiresAt = Timestamp.fromMillis(
        now.toMillis() + ANNONCE_DURATION_DAYS * 24 * 60 * 60 * 1000
      );

      const annonceData = {
        authorId: userId,
        title: data.title.trim(),
        description: data.description.trim(),
        category: data.category,
        price: data.price ?? null,
        priceLabel: data.priceLabel ?? null,
        city: data.city.trim(),
        photos: data.photos || [],
        featured: null,
        views: 0,
        contactCount: 0,
        status: 'active',
        expiresAt,
        createdAt: serverTimestamp(),
      };

      const docRef = await addDoc(collection(db, 'annonces'), annonceData);
      return { success: true, data: { id: docRef.id, ...annonceData } };
    } catch (err) {
      console.error('annonceService.createAnnonce error:', err);
      return { success: false, error: err.message };
    }
  },

  // ── Mettre à jour une annonce ─────────────────────────
  async updateAnnonce(annonceId, userId, data) {
    try {
      const annonceRef = doc(db, 'annonces', annonceId);
      const snap = await getDoc(annonceRef);
      if (!snap.exists()) {
        return { success: false, error: 'not_found' };
      }
      if (snap.data().authorId !== userId) {
        return { success: false, error: 'not_owner' };
      }

      // Filtrer les champs protégés
      const {
        authorId: _a,
        createdAt: _c,
        views: _v,
        contactCount: _cc,
        featured: _f,
        status: _s,
        ...allowed
      } = data;

      if (allowed.description && allowed.description.length > ANNONCE_MAX_DESCRIPTION) {
        return { success: false, error: 'description_too_long' };
      }
      if (allowed.photos && allowed.photos.length > ANNONCE_MAX_PHOTOS) {
        return { success: false, error: 'too_many_photos' };
      }

      await updateDoc(annonceRef, {
        ...allowed,
        updatedAt: serverTimestamp(),
      });

      return { success: true };
    } catch (err) {
      console.error('annonceService.updateAnnonce error:', err);
      return { success: false, error: err.message };
    }
  },

  // ── Supprimer une annonce ─────────────────────────────
  async deleteAnnonce(annonceId, userId) {
    try {
      const annonceRef = doc(db, 'annonces', annonceId);
      const snap = await getDoc(annonceRef);
      if (!snap.exists()) {
        return { success: false, error: 'not_found' };
      }
      if (snap.data().authorId !== userId) {
        return { success: false, error: 'not_owner' };
      }

      await deleteDoc(annonceRef);
      return { success: true };
    } catch (err) {
      console.error('annonceService.deleteAnnonce error:', err);
      return { success: false, error: err.message };
    }
  },

  // ── Récupérer une annonce par ID ──────────────────────
  async getAnnonce(annonceId) {
    try {
      const snap = await getDoc(doc(db, 'annonces', annonceId));
      if (!snap.exists()) {
        return { success: false, error: 'not_found' };
      }
      return { success: true, data: { id: snap.id, ...snap.data() } };
    } catch (err) {
      console.error('annonceService.getAnnonce error:', err);
      return { success: false, error: err.message };
    }
  },

  // ── Lister les annonces actives (avec filtres) ────────
  async getAnnonces(filters = {}) {
    try {
      const constraints = [where('status', '==', 'active')];

      if (filters.category) {
        constraints.push(where('category', '==', filters.category));
      }
      if (filters.city) {
        constraints.push(where('city', '==', filters.city));
      }

      constraints.push(orderBy('createdAt', 'desc'));

      const q = query(collection(db, 'annonces'), ...constraints);
      const snap = await getDocs(q);

      let annonces = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

      // Filtrer les annonces expirées côté client
      // TODO : Cloud Function schedulée pour expirer automatiquement
      const now = new Date();
      annonces = annonces.filter((a) => {
        if (!a.expiresAt) return true;
        const expiresDate = a.expiresAt.toDate ? a.expiresAt.toDate() : new Date(a.expiresAt);
        return expiresDate > now;
      });

      // Filtre search côté client (titre)
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        annonces = annonces.filter((a) => a.title.toLowerCase().includes(searchLower));
      }

      // Tri : "À la une" en premier
      annonces.sort((a, b) => {
        const aFeatured = a.featured && a.featured.type === 'une' ? 1 : 0;
        const bFeatured = b.featured && b.featured.type === 'une' ? 1 : 0;
        return bFeatured - aFeatured;
      });

      return { success: true, data: annonces };
    } catch (err) {
      console.error('annonceService.getAnnonces error:', err);
      return { success: false, error: err.message };
    }
  },

  // ── Lister les annonces d'un utilisateur ──────────────
  async getUserAnnonces(userId) {
    try {
      const q = query(
        collection(db, 'annonces'),
        where('authorId', '==', userId),
        orderBy('createdAt', 'desc')
      );
      const snap = await getDocs(q);
      return {
        success: true,
        data: snap.docs.map((d) => ({ id: d.id, ...d.data() })),
      };
    } catch (err) {
      console.error('annonceService.getUserAnnonces error:', err);
      return { success: false, error: err.message };
    }
  },

  // ── Renouveler une annonce expirée ────────────────────
  async renewAnnonce(annonceId, userId) {
    try {
      const annonceRef = doc(db, 'annonces', annonceId);
      const snap = await getDoc(annonceRef);
      if (!snap.exists()) {
        return { success: false, error: 'not_found' };
      }
      if (snap.data().authorId !== userId) {
        return { success: false, error: 'not_owner' };
      }

      // Vérifier max 5 actives (sauf si celle-ci est déjà active)
      if (snap.data().status !== 'active') {
        const activeQuery = query(
          collection(db, 'annonces'),
          where('authorId', '==', userId),
          where('status', '==', 'active')
        );
        const activeSnap = await getDocs(activeQuery);
        if (activeSnap.size >= ANNONCE_MAX_ACTIVE) {
          return { success: false, error: 'max_active_reached' };
        }
      }

      const now = Timestamp.now();
      const expiresAt = Timestamp.fromMillis(
        now.toMillis() + ANNONCE_DURATION_DAYS * 24 * 60 * 60 * 1000
      );

      await updateDoc(annonceRef, {
        status: 'active',
        expiresAt,
        updatedAt: serverTimestamp(),
      });

      return { success: true };
    } catch (err) {
      console.error('annonceService.renewAnnonce error:', err);
      return { success: false, error: err.message };
    }
  },

  // ── Upload photo annonce (resize 800px, JPEG 0.7) ────
  async uploadAnnoncePhoto(userId, file) {
    try {
      const blob = await new Promise((resolve, reject) => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = new Image();
        img.onload = () => {
          URL.revokeObjectURL(img.src);
          let w = img.width,
            h = img.height;
          const max = 800;
          if (w > h) {
            if (w > max) {
              h = (h * max) / w;
              w = max;
            }
          } else {
            if (h > max) {
              w = (w * max) / h;
              h = max;
            }
          }
          canvas.width = w;
          canvas.height = h;
          ctx.drawImage(img, 0, 0, w, h);
          canvas.toBlob(resolve, 'image/jpeg', 0.7);
        };
        img.onerror = () => {
          URL.revokeObjectURL(img.src);
          reject(new Error('Format image invalide'));
        };
        img.src = URL.createObjectURL(file);
      });
      const ts = Date.now();
      const rand = Math.random().toString(36).slice(2, 8);
      const sRef = ref(storage, `annonce_pictures/${userId}/${ts}_${rand}.jpg`);
      await uploadBytes(sRef, blob);
      const url = await getDownloadURL(sRef);
      return { success: true, url };
    } catch (err) {
      console.error('annonceService.uploadAnnoncePhoto error:', err);
      return { success: false, error: "Erreur lors de l'envoi de l'image" };
    }
  },

  // ── Incrémenter les vues (Cloud Function callable) ────
  async incrementViews(annonceId) {
    try {
      const fn = httpsCallable(functions, 'incrementAnnonceViews');
      const result = await fn({ annonceId });
      return result.data;
    } catch (err) {
      console.error('annonceService.incrementViews error:', err);
      return { success: false, error: err.message };
    }
  },

  // ── Incrémenter le compteur de contacts (Cloud Function callable) ──
  async incrementContactCount(annonceId) {
    try {
      const fn = httpsCallable(functions, 'incrementAnnonceContactCount');
      const result = await fn({ annonceId });
      return result.data;
    } catch (err) {
      console.error('annonceService.incrementContactCount error:', err);
      return { success: false, error: err.message };
    }
  },
};

export default annonceService;
